"use client";

import { useState } from "react";
import { deriveContext } from "@/lib/feedbackContext";
import { phContext, track } from "@/lib/analytics";

type Category = "not_working" | "incorrect_info" | "suggestion";

const CATEGORIES: { id: Category; label: string }[] = [
  { id: "not_working", label: "Not working" },
  { id: "incorrect_info", label: "Incorrect info" },
  { id: "suggestion", label: "Suggestion" },
];

const PLACEHOLDER: Record<Category, string> = {
  not_working: "What isn't working? What did you expect to happen?",
  incorrect_info: "What's incorrect, and what should it say?",
  suggestion: "What would make this better?",
};

/**
 * The feedback form itself — category + free text + optional email. Posts JSON to
 * /api/feedback (kind = site_feedback), auto-attaching page context and the
 * PostHog distinct/session id so the report stitches to the person's journey.
 * Reused inline by PageFeedback and standalone on the /feedback page.
 */
export function FeedbackForm({
  resourceType,
  defaultCategory = "suggestion",
}: {
  /** Known section ("tool" | "guide" | "template" | "resource"); inferred from URL if omitted. */
  resourceType?: string;
  defaultCategory?: Category;
}) {
  const [category, setCategory] = useState<Category>(defaultCategory);
  const [comments, setComments] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  async function submit() {
    const text = comments.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(false);
    const ctx = deriveContext(resourceType);
    const ph = phContext();
    track("feedback_submitted", { category, resource_type: ctx.resourceType, slug: ctx.slug });
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          comments: text,
          email: email.trim(),
          resource_type: ctx.resourceType,
          slug: ctx.slug,
          page_url: window.location.href,
          page_title: document.title,
          ph_distinct_id: ph.ph_distinct_id,
          ph_session_id: ph.ph_session_id,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-2xl border border-line bg-white p-5">
        <p className="font-display text-base font-semibold text-ink">Thanks — we&apos;re on it.</p>
        <p className="mt-1.5 text-sm leading-relaxed text-body">
          We read every report and use it to fix and improve the content.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-3 rounded-2xl border border-line bg-white p-5"
    >
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategory(c.id)}
            aria-pressed={category === c.id}
            className={`rounded-full border px-3.5 py-1.5 text-[0.8rem] font-medium transition-colors ${
              category === c.id
                ? "border-emerald bg-emerald/10 text-ink"
                : "border-line text-body hover:border-emerald/50 hover:text-ink"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <textarea
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        placeholder={PLACEHOLDER[category]}
        maxLength={4000}
        rows={4}
        className="w-full resize-none rounded-xl border border-line bg-white px-3.5 py-3 text-[0.88rem] leading-relaxed text-ink outline-none placeholder:text-muted/60 focus:border-emerald"
      />

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email (optional — if you'd like a reply)"
        maxLength={200}
        className="w-full rounded-full border border-line bg-white px-4 py-2.5 text-[0.88rem] text-ink outline-none placeholder:text-muted/60 focus:border-emerald"
      />

      {error && (
        <p className="text-[0.8rem] text-risk-veryhigh">
          Couldn&apos;t send — try again, or email hello@ramsdocs.co.uk.
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !comments.trim()}
        className="rounded-full bg-emerald px-6 py-2.5 text-[0.85rem] font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-50"
      >
        {busy ? "Sending…" : "Send feedback"}
      </button>
    </form>
  );
}
